import { useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAvailability } from '../api/queries'
import { ErrorNotice, SuggestionList } from '../components/ErrorNotice'
import { Button, Empty, Field, Input, PageTitle, Select, Spinner } from '../components/ui'
import { cn } from '../lib/cn'
import { seats } from '../lib/words'
import {
  atWallTime,
  daysFromToday,
  DEFAULT_DURATION_MINUTES,
  fmtDuration,
  fmtTime,
  nextHalfHour,
  pad,
  todayKey,
  toIso,
  wallTimeOptions,
} from '../lib/time'
import { useCanBook, useSession } from '../session'

/**
 * Экран 2.2 «Поиск по времени» — первый сценарий заказчика: время известно, комната нет.
 *
 * Список пересчитывается сразу при изменении полей, кнопки «Найти» нет: лишнее нажатие
 * между «хочу в час» и «вот что свободно» ничего не добавляет.
 *
 * Занятые комнаты остаются в списке приглушёнными и с причиной — человек должен видеть,
 * что комната существует и почему сейчас недоступна, а не гадать, куда она делась.
 */
export function SearchByTimePage() {
  const [params] = useSearchParams()
  const { policy } = useSession()
  const canBook = useCanBook()
  const navigate = useNavigate()

  const [date, setDate] = useState(params.get('date') ?? todayKey())
  const [startTime, setStartTime] = useState(() => defaultStart(policy.work_start, policy.work_end))
  const [duration, setDuration] = useState(DEFAULT_DURATION_MINUTES)
  const [minCapacity, setMinCapacity] = useState<number>(0)

  const startsAt = useMemo(() => toIso(atWallTime(date, startTime)), [date, startTime])
  const endsAt = useMemo(
    () => toIso(new Date(atWallTime(date, startTime).getTime() + duration * 60_000)),
    [date, startTime, duration],
  )

  const horizonExceeded = daysFromToday(date) > policy.horizon_days
  const durationExceeded = duration > policy.max_duration_minutes
  const inputsValid = !horizonExceeded && !durationExceeded

  const { data, isFetching, error } = useAvailability(
    startsAt,
    endsAt,
    minCapacity || undefined,
    inputsValid,
  )

  const free = data?.rooms.filter((row) => row.available) ?? []
  const busy = data?.rooms.filter((row) => !row.available) ?? []

  return (
    <>
      <PageTitle>Когда нужна переговорная?</PageTitle>

      <div className="card mb-5 grid gap-4 p-4 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="Дата" error={horizonExceeded ? horizonMessage(policy.horizon_days) : undefined}>
          <Input
            type="date"
            className="tnum"
            value={date}
            min={todayKey()}
            onChange={(event) => setDate(event.target.value)}
          />
        </Field>

        <Field label="Начало">
          <Select value={startTime} onChange={(event) => setStartTime(event.target.value)}>
            {wallTimeOptions(policy.work_start, policy.work_end).map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Select>
        </Field>

        <Field
          label="Длительность"
          error={
            durationExceeded
              ? `Дольше ${fmtDuration(policy.max_duration_minutes)} бронировать нельзя. Выберите меньше или разбейте встречу.`
              : undefined
          }
        >
          <Select value={duration} onChange={(event) => setDuration(Number(event.target.value))}>
            {durationOptions(policy.max_duration_minutes).map((minutes) => (
              <option key={minutes} value={minutes}>
                {fmtDuration(minutes)}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Не меньше человек" hint="Отсекает комнаты, куда все не поместятся">
          <Select value={minCapacity} onChange={(event) => setMinCapacity(Number(event.target.value))}>
            <option value={0}>Любая вместимость</option>
            {[3, 5, 10, 15, 20].map((value) => (
              <option key={value} value={value}>
                от {value}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      <div className="mb-3 flex items-center gap-2 text-sm text-muted">
        <span className="tnum">
          {fmtTime(startsAt)} – {fmtTime(endsAt)}
        </span>
        {isFetching && <Spinner className="h-3.5 w-3.5" />}
      </div>

      {error && <ErrorNotice error={error} className="mb-4" />}

      {/* Предупреждение по запросу целиком: смотреть можно, забронировать нельзя.
          Сервер намеренно не отказывает — например, когда длительность выносит конец
          за конец рабочего дня, — чтобы человек узнал об этом здесь, а не после
          заполнения формы. Промолчать тут значит вернуть ему тот самый поздний отказ. */}
      {inputsValid && data?.warning && (
        <div
          role="status"
          className="card mb-4 border-state-soon/50 bg-state-soon/10 px-4 py-3 text-sm"
        >
          <p className="font-medium">Забронировать это время не получится</p>
          <p className="mt-1 text-muted">{data.warning}</p>
          <p className="mt-1 text-muted">
            Свободные комнаты ниже показаны, чтобы было видно, что вообще занято.
          </p>
        </div>
      )}

      {inputsValid && data && free.length === 0 && (
        /* Экран 2.8: вместо пустоты — ближайшие варианты. */
        <div className="card mb-4 p-4">
          <p className="font-medium">В это время всё занято</p>
          <p className="mt-1 hint">
            Ниже видно, кем и до какого времени. Если ждать нельзя, вот ближайшие свободные окна.
          </p>
          {data.suggestions && data.suggestions.length > 0 ? (
            <SuggestionList
              className="mt-4"
              heading="Ближайшие варианты:"
              suggestions={data.suggestions}
              onPick={(suggestion) =>
                navigate(
                  `/book?room=${suggestion.room_id}&start=${encodeURIComponent(suggestion.starts_at)}&end=${encodeURIComponent(suggestion.ends_at)}`,
                )
              }
            />
          ) : (
            <p className="mt-3 text-sm text-muted">
              Попробуйте сдвинуть время или посмотреть следующий день.
            </p>
          )}
        </div>
      )}

      <div className="flex flex-col gap-2">
        {free.map((row) => (
          <div
            key={row.room.id}
            className="card flex flex-wrap items-center justify-between gap-3 px-4 py-3"
          >
            <div className="min-w-0">
              <p className="font-medium">{row.room.name}</p>
              <p className="text-sm text-muted">
                {seats(row.room.capacity)}
                {row.room.equipment.length > 0 && ` · ${row.room.equipment.join(', ')}`}
              </p>
            </div>
            <Button
              variant="primary"
              disabled={!canBook}
              title={canBook ? undefined : 'Бронирование вам сейчас недоступно'}
              onClick={() =>
                navigate(
                  `/book?room=${row.room.id}&start=${encodeURIComponent(startsAt)}&end=${encodeURIComponent(endsAt)}`,
                )
              }
            >
              Выбрать
            </Button>
          </div>
        ))}

        {busy.map((row) => (
          <div
            key={row.room.id}
            className={cn('card flex flex-wrap items-center justify-between gap-3 px-4 py-3 opacity-60')}
          >
            <div className="min-w-0">
              <p className="font-medium">{row.room.name}</p>
              <p className="tnum text-sm text-muted">
                {row.reason ??
                  (row.busy_until ? `Занята до ${fmtTime(row.busy_until)}` : 'Занята в это время')}
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate(`/rooms/${row.room.id}?date=${date}`)}>
              Другие слоты
            </Button>
          </div>
        ))}
      </div>

      {inputsValid && data && data.rooms.length === 0 && (
        <Empty
          title="Комнат нет"
          hint="Ни одной видимой переговорной не заведено. Это делает администратор в разделе «Управление»."
        />
      )}
    </>
  )
}

function defaultStart(workStart: string, workEnd: string): string {
  const suggested = nextHalfHour()
  const value = `${pad(suggested.getHours())}:${pad(suggested.getMinutes())}`
  const options = wallTimeOptions(workStart, workEnd)
  // Если рабочий день уже закончился, предлагаем начало следующего: показывать
  // «21:30» в списке, где такого варианта нет, — верный способ запутать.
  return options.includes(value) ? value : (options[0] ?? workStart)
}

function durationOptions(maxMinutes: number): number[] {
  const options: number[] = []
  for (let minutes = 30; minutes <= Math.max(30, maxMinutes); minutes += 30) options.push(minutes)
  return options
}

function horizonMessage(horizonDays: number): string {
  return `Бронировать можно не дальше чем на ${horizonDays} дней вперёд. Выберите дату поближе.`
}
